import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Button } from "../ui/button";
import { Plus } from "lucide-react";
import { ActionItem, ActionItemFormData } from "../../types/actionItems";
import { ActionItemCard } from "../action-item/ActionItemCard";
import { ActionItemForm } from "../action-item/ActionItemForm";
import { useActionItemOperations } from "../../hooks/useActionItemOperations";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { useToast } from "../../hooks/use-toast";
import { supabase } from "../../lib/supabase";
import React from "react";

interface ActionItemsSectionProps {
  actionItems: ActionItem[];
  meetingId?: string;
  userId?: string;
  onActionItemChange: () => void;
}

export function ActionItemsSection({
  actionItems = [],
  meetingId,
  userId,
  onActionItemChange,
}: ActionItemsSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createActionItem, updateActionItem, deleteActionItem, isLoading } = useActionItemOperations(meetingId, userId);

  // Set up real-time subscription for action items
  useEffect(() => {
    if (!meetingId) return;

    const channel = supabase
      .channel(`action-items-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'action_items',
          filter: `meeting_id=eq.${meetingId}`,
        },
        () => {
          onActionItemChange();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [meetingId, onActionItemChange]);

  const handleCreateActionItem = async (formData: ActionItemFormData) => {
    const newItem = await createActionItem(formData);
    if (newItem) {
      setShowForm(false);
      onActionItemChange();
      toast({
        title: "Success",
        description: "Action item created successfully",
      });
    }
  };

  // Sort action items by due date first (ascending), then by created date (descending)
  const sortedActionItems = [...actionItems].sort((a, b) => {
    // If both items have due dates, compare them
    if (a.due_date && b.due_date) {
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    }
    // If only one item has a due date, it should come first
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    // If neither has a due date, sort by created date (descending)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const handleItemClick = (itemId: string) => {
    navigate(`/app/action-items/${itemId}`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <h3 className="text-lg font-semibold">Action Items</h3>
        <Sheet open={showForm} onOpenChange={setShowForm}>
          <SheetTrigger asChild>
            <Button 
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Action Item
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>New Action Item</SheetTitle>
            </SheetHeader>
            <div className="mt-2 text-sm text-gray-500">
              Add a new task so you don't forget to do something later on.
            </div>
            <div className="mt-6">
              <ActionItemForm
                onSubmit={handleCreateActionItem}
                onCancel={() => setShowForm(false)}
                isLoading={isLoading}
              />
            </div>
          </SheetContent>
        </Sheet>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sortedActionItems.length > 0 ? (
            <div className="space-y-4">
              {sortedActionItems.map((item) => (
                <ActionItemCard
                  key={item.id}
                  item={item}
                  onClick={() => handleItemClick(item.id)}
                  meetingTitle={meetingId ? "From this meeting" : undefined}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-neutral-500 py-4">
              No action items yet
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}